<template>
    <InfoBaseForm v-model="formData" :is-loading="isLoading" @save="save()" @cancel="emit('cancel')">
        <template v-slot:center-block>
            <v-row dense class="mb-4">
                <v-col>
                    <v-select
                        v-model="formData.currencyCode"
                        :items="useCurrency"
                        :label="$t('currency')"
                        clearable
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="currency"
                        class="width-max-content"
                        hide-details
                        min-width="100"
                    />
                </v-col>
            </v-row>
            <v-row dense>
                <v-col cols="12" sm="6" md="4">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="formData.franchice"
                                type="number"
                                :label="`${$t('franchise')}*`"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="franchise"
                                :rules="[requiredRule('franchise')]"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="formData.amountCoverage"
                                type="number"
                                :label="`${$t('coverage_amount')}*`"
                                hide-spin-buttons
                                variant="outlined"
                                color="primary"
                                density="compact"
                                data-cy="coverage-amount"
                                :rules="[requiredRule('coverage_amount')]"
                            />
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import {requiredRule} from '~/composables/rules'
    import {initInsuranceService} from '~/utils/types/account/products/helpers'
    import InfoBaseForm from '~src/components/products/information/InfoBaseForm.vue'
    import {useCurrency} from '~/composables/form-helpers'

    const props = defineProps<{
        contractId: number
        editableServiceId: number | null
    }>()

    const emit = defineEmits<{
        (e: 'save'): void
        (e: 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const isLoading = ref<boolean>(false)
    const formData = ref(initInsuranceService())

    onMounted(async () => {
        if (props.editableServiceId) await loadData()
    })

    async function loadData(): Promise<void> {
        try {
            isLoading.value = true
            const rq = {id: props.editableServiceId, locale: 'all'}
            const {
                apiOwnInsuranceServices: [_insuranceService],
            } = await app.$api.ownInsuranceServices.get(rq)
            formData.value = _insuranceService
        } catch (e) {
            formData.value = initInsuranceService()
        } finally {
            isLoading.value = false
        }
    }

    const save = async (): Promise<void> => {
        try {
            isLoading.value = true
            const data = clone(formData.value)
            clean(data)
            if (props.editableServiceId) {
                await app.$api.ownInsuranceServices.put({
                    contractId: props.contractId,
                    ...data,
                })
            } else {
                await app.$api.ownInsuranceServices.post({
                    contractId: props.contractId,
                    ...data,
                })
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }
</script>
